import { Link } from '#components/atoms/link/Link.tsx';
import { Snippet } from '#components/patterns/code/snippet/Snippet.tsx';
import type { DesktopOSName } from '#dn-types/downloads.ts';
import type { HostNoun } from '#dn-types/host.ts';
import type { EnrollmentState } from '../../types.ts';
import { BINARY_SETUP_COMMANDS } from '../binary-install.ts';
import { chainedPackageInstall, type LinuxInstallMethod } from '../package-install.ts';
import { buildEnrollCommand } from './build-enroll-command.ts';
import { ExpandButton } from './ExpandButton.tsx';
import { TokenStatus } from './TokenStatus.tsx';

type Props = {
  hostNoun: HostNoun;
  enrollmentState: EnrollmentState;
  platform: Exclude<DesktopOSName, 'Windows'>;
  /** This step's number in the parent page's layout — the parent knows which steps precede it. */
  stepNumber: number;
  downloadCommand?: string;
  extraInstructions?: string;
  installMethod?: LinuxInstallMethod;
  onExpand: () => void;
  /** Controls that must stay usable while condensed (e.g. the Linux arch select), rendered above the commands. */
  children?: React.ReactNode;
};

export function CollapsedInstructions({
  hostNoun,
  platform,
  enrollmentState,
  stepNumber,
  downloadCommand,
  extraInstructions,
  installMethod = 'binary',
  onExpand,
  children,
}: Props) {
  const { token, isExpired, enrollSuccessful, handleDone } = enrollmentState;
  const packageManager = installMethod === 'binary' ? undefined : installMethod;

  let commands: string[];
  if (packageManager) {
    commands = [...chainedPackageInstall(packageManager), buildEnrollCommand(platform, token, packageManager)];
  } else {
    commands = [
      `${BINARY_SETUP_COMMANDS.makeExecutable} && \\`,
      `${BINARY_SETUP_COMMANDS.installService} && \\`,
      `${BINARY_SETUP_COMMANDS.startService} && \\`,
      buildEnrollCommand(platform, token),
    ];

    if (downloadCommand) {
      commands = [`${downloadCommand} && \\`, ...commands];
    }
  }

  return (
    <>
      <h2 className="h3">
        {stepNumber}. {packageManager ? 'Install' : 'Download'} dnclient and enroll {hostNoun}
      </h2>
      {extraInstructions && <p className="p-md mb-24">{extraInstructions}</p>}
      {children}
      {/* Without a download command the chain starts at chmod, which only works after a manual
          download — say so instead of presenting a silently truncated one-paste chain. */}
      {!packageManager && !downloadCommand && (
        <p className="p-md">
          Download dnclient from the <Link to="/downloads">Downloads</Link> page and move it to the {hostNoun} being
          enrolled, then run the following in the same directory.
        </p>
      )}
      {/* Disabled while the token loads: the chain would otherwise paste as runnable shell that
          installs everything and then fails confusingly at `enroll -code <loading…>`. */}
      <Snippet
        disabled={!token || isExpired}
        breakWord={true}
        addPrompt={false}
        secondaryButton={<ExpandButton onClick={onExpand} />}
      >
        {commands}
      </Snippet>
      <TokenStatus hostNoun={hostNoun} enrollSuccessful={enrollSuccessful} handleDone={handleDone} />
    </>
  );
}
